/**
 * O fim da partida.
 *
 * Uma decisão de produto governa este arquivo, e ela é contraintuitiva:
 * **vitória e derrota têm o mesmo peso visual**. Mesmo tamanho de letra,
 * mesma área na tela, mesma clareza no número.
 *
 * A tentação de fazer diferente é grande e tem nome. Comemorar o ganho com
 * animação e recolher a perda num aviso discreto é o padrão de máquina de
 * cassino: quem ganhou lembra vívido, quem perdeu mal registra, e o
 * resultado é uma memória enviesada que faz a pessoa acreditar que está
 * indo melhor do que está. Num produto onde se perde dinheiro de verdade,
 * isso não é entusiasmo — é enganar o jogador com a própria memória dele.
 *
 * Por isso: sem confete, sem som, sem estouro. A cor distingue os
 * desfechos; o resto é idêntico. E os dois trazem o mesmo botão para
 * jogar de novo, porque esconder essa saída de quem perdeu seria a mesma
 * manipulação ao contrário.
 *
 * Sobre o foco: este é um diálogo modal de verdade, não um cartão
 * flutuante. Sem prender o foco, quem navega por teclado sai dele na
 * primeira tabulação e vai parar nos botões do tabuleiro atrás — que
 * ainda respondem, num jogo que já acabou. Escape fecha, como em qualquer
 * diálogo.
 */
import { useEffect, useRef } from 'react';

import { m } from 'motion/react';

import { Avatar } from './Avatar.tsx';
import { money } from '../lib/format.ts';

export type TipoDesfecho = 'vitoria' | 'derrota' | 'empate';

export interface DesfechoProps {
  readonly tipo: TipoDesfecho;
  /** Motivo já em português: "xeque-mate", "tempo esgotado". */
  readonly motivo: string;
  /**
   * Quanto entrou ou saiu do saldo, com sinal. Zero num empate, onde a
   * entrada volta inteira e ninguém ganhou nada.
   */
  readonly variacaoCents: number;
  /** Quanto a casa reteve. Só aparece quando houve retenção. */
  readonly rakeCents?: number;
  readonly adversario?: {
    readonly displayName: string;
    readonly avatar: string;
    readonly avatarColor: string;
  } | null;
  readonly onJogarDeNovo?: () => void;
  readonly onSair: () => void;
}

const TITULO: Record<TipoDesfecho, string> = {
  vitoria: 'Você venceu',
  derrota: 'Você perdeu',
  empate: 'Empate',
};

const COR: Record<TipoDesfecho, string> = {
  vitoria: 'var(--primary)',
  derrota: 'var(--danger)',
  empate: 'var(--muted-foreground)',
};

const FOCAVEIS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function Desfecho({
  tipo,
  motivo,
  variacaoCents,
  rakeCents,
  adversario,
  onJogarDeNovo,
  onSair,
}: DesfechoProps) {
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    // Foco no primeiro botão do diálogo, não na caixa: quem usa leitor de
    // tela ouve o título pelo `aria-labelledby` e já chega numa ação.
    caixa.current?.querySelector<HTMLElement>(FOCAVEIS)?.focus();

    function aoTeclar(evento: KeyboardEvent): void {
      if (evento.key === 'Escape') {
        evento.preventDefault();
        onSair();
        return;
      }
      if (evento.key !== 'Tab') return;

      const alvos = [...(caixa.current?.querySelectorAll<HTMLElement>(FOCAVEIS) ?? [])];
      if (alvos.length === 0) return;

      const primeiro = alvos[0] as HTMLElement;
      const ultimo = alvos[alvos.length - 1] as HTMLElement;
      const atual = document.activeElement;

      // Circula dentro do diálogo em vez de escapar para o tabuleiro
      // atrás, que continua respondendo a clique de um jogo encerrado.
      if (evento.shiftKey && (atual === primeiro || !caixa.current?.contains(atual))) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && atual === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    }

    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      anterior?.focus?.();
    };
  }, [onSair]);

  return (
    <m.div
      className="fixed inset-0 z-50 grid place-items-center px-4"
      style={{
        background: 'color-mix(in oklab, var(--background) 88%, transparent)',
        backdropFilter: 'blur(6px)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.24 }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="desfecho-titulo"
    >
      <m.div
        ref={caixa}
        className="card w-full max-w-md p-7 grid gap-6 text-center"
        style={{ boxShadow: 'var(--shadow-raised)', borderTop: `3px solid ${COR[tipo]}` }}
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.2, 0.9, 0.3, 1] }}
      >
        <div className="grid gap-2">
          <h2
            id="desfecho-titulo"
            className="font-bold"
            style={{ fontSize: 'var(--text-display)', lineHeight: 1.1, color: COR[tipo] }}
          >
            {TITULO[tipo]}
          </h2>
          <p className="text-muted-foreground">Por {motivo}.</p>
        </div>

        <div className="grid gap-1 py-2 border-y" style={{ borderColor: 'var(--border)' }}>
          {/* Não repete o título. O que muda entre os dois blocos é a
              pergunta que respondem: em cima, quem ganhou; aqui, quanto
              se mexeu no saldo. */}
          <span className="rotulo">
            {tipo === 'empate'
              ? 'Sua entrada'
              : variacaoCents >= 0
                ? 'Entrou no seu saldo'
                : 'Saiu do seu saldo'}
          </span>
          <span
            className="money font-bold tabular-nums"
            style={{ fontSize: 'var(--text-display)', color: COR[tipo] }}
          >
            {tipo === 'empate'
              ? 'devolvida'
              : `${variacaoCents > 0 ? '+' : ''}${money(variacaoCents)}`}
          </span>
          {rakeCents !== undefined && rakeCents > 0 ? (
            <span className="text-xs text-muted-foreground">
              Já descontada a comissão da casa, de {money(rakeCents)}.
            </span>
          ) : null}
          {tipo === 'empate' ? (
            <span className="text-xs text-muted-foreground">
              Empate não tem comissão: cada um recebe de volta o que colocou.
            </span>
          ) : null}
        </div>

        {adversario ? (
          <div className="flex items-center justify-center gap-3">
            <Avatar
              avatar={adversario.avatar}
              cor={adversario.avatarColor}
              tamanho={32}
              nome={adversario.displayName}
            />
            <span className="text-sm text-muted-foreground truncate">
              contra {adversario.displayName}
            </span>
          </div>
        ) : null}

        <div className="grid gap-2">
          {onJogarDeNovo ? (
            <button type="button" className="btn btn-primary !py-3" onClick={onJogarDeNovo}>
              Jogar de novo
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onSair}>
            Sair da partida
          </button>
        </div>
      </m.div>
    </m.div>
  );
}
